// playVoiceline — fire-and-forget voiceline playback for cooking
// triggers (timer start, timer done, step advance, etc).
//
// Looks up the cached MP3 in Supabase Storage at the deterministic
// path /recipe-audio/voicelines/<voiceId>/<phraseKey>.mp3, plays it
// via the Web Audio context that chadwickAudio.js already has
// unlocked, and dispatches the same `chadwick:speak-start` /
// `chadwick:speak-end` events that BackgroundMusic listens for —
// so background music ducks under voiceline playback the same way
// it does for chef audio + per-step TTS.
//
// Silent no-op when:
//   - voiceId is missing (recipe didn't generate narration → we
//     don't know which voice to use, so no voiceline plays)
//   - phraseKey isn't in the catalog
//   - the MP3 hasn't been baked yet (404 from Storage)
//
// The 404 case is graceful by design: an admin who hasn't run the
// bulk-generate flow for a new phrase shouldn't see errors — the
// triggers just stay silent until the cache is filled.

import { supabase } from "./supabase";
import { voicelineUrl, VOICELINES } from "../data/voicelines";
import { claim as claimFloor, extend as extendFloor, release as releaseFloor } from "./audioFloor";
import { playEngineNarration, stopEngineNarration, engineNarrationSupported } from "./engineNarration";

// activeAudio holds the live HTMLAudioElement on web, or the sentinel
// string "engine" while a Swift-side narrationPlayer clip is in flight.
let activeAudio = null;
// Tracks whether the active call has a live floor claim. Each play
// claims at most once and releases at most once, even if multiple
// audio events fire (ended after error, etc.).
let activeClaimed = false;

function releaseIfClaimed() {
  if (!activeClaimed) return;
  activeClaimed = false;
  releaseFloor();
}

export function playVoiceline(phraseKey, voiceId) {
  if (!phraseKey || !voiceId) return;
  if (!VOICELINES[phraseKey]) {
    console.warn(`[playVoiceline] unknown phraseKey: ${phraseKey}`);
    return;
  }
  const url = voicelineUrl(supabase, voiceId, phraseKey);
  if (!url) return;

  // Hard-stop any prior voiceline so two triggers firing close together
  // don't talk over each other. Releases its floor claim along with it.
  if (activeAudio === "engine") {
    stopEngineNarration().catch(() => {});
    activeAudio = null;
    releaseIfClaimed();
  } else if (activeAudio) {
    try { activeAudio.pause(); } catch { /* ignore */ }
    try { activeAudio.src = ""; } catch { /* ignore */ }
    activeAudio = null;
    releaseIfClaimed();
  }

  // Engine path on iOS native: narration plays through the same
  // AVAudioEngine as the recognizer so VPIO can subtract it from
  // the mic input. No web <audio> involvement.
  //
  // Floor claim deferred until the engine actually starts rendering.
  // Claiming up-front (the prior pattern) fired chadwick:speak-start
  // for clips that silently failed to play (Web Audio interrupted,
  // engine race, missing file) — the user perceived "music ducking
  // with nobody talking." Now the claim rides on a successful play.
  if (engineNarrationSupported()) {
    activeAudio = "engine";
    // We don't have a per-clip "started playing" event on the engine
    // path; playEngineNarration resolves only on natural end /
    // supersede. As a proxy, claim AFTER a brief microtask so a
    // synchronous-failure case (which would reject the promise
    // immediately) doesn't claim+release a spurious duck. If the
    // promise resolves/rejects before this microtask, the activeAudio
    // check short-circuits.
    queueMicrotask(() => {
      if (activeAudio !== "engine") return;
      claimFloor(30000);
      activeClaimed = true;
    });
    playEngineNarration(url).then(() => {
      if (activeAudio === "engine") activeAudio = null;
      releaseIfClaimed();
    }).catch((err) => {
      console.warn("[playVoiceline] engine path failed:", err?.message || err);
      if (activeAudio === "engine") activeAudio = null;
      releaseIfClaimed();
    });
    return;
  }

  const audio = new Audio(url);
  activeAudio = audio;
  audio.preload = "auto";
  // Claim the floor on the actual `playing` event — the moment HTML5
  // audio confirms it's rendering. If autoplay is blocked, the
  // network 404s, or the ctx is interrupted, `playing` never fires
  // and no spurious duck happens.
  audio.addEventListener("playing", () => {
    if (activeAudio !== audio) return;
    if (!activeClaimed) {
      claimFloor(30000);
      activeClaimed = true;
    }
    const dur = Number.isFinite(audio.duration) ? audio.duration : 0;
    if (dur > 0) extendFloor(Math.ceil(dur * 1000) + 1500);
  });
  const end = () => {
    if (activeAudio === audio) activeAudio = null;
    releaseIfClaimed();
  };
  audio.addEventListener("ended", end);
  audio.addEventListener("error", end);
  audio.play().catch(() => {
    // Autoplay blocked or 404 — no `playing` event fires so no claim
    // was made. Clear the active reference; nothing to release.
    if (activeAudio === audio) activeAudio = null;
  });
}

// Stop the active voiceline (e.g. user navigated away mid-playback).
// Idempotent.
export function stopVoiceline() {
  if (!activeAudio) return;
  if (activeAudio === "engine") {
    stopEngineNarration().catch(() => {});
  } else {
    try { activeAudio.pause(); } catch { /* ignore */ }
    try { activeAudio.src = ""; } catch { /* ignore */ }
  }
  activeAudio = null;
  releaseIfClaimed();
}
